import React, { useState, useRef } from "react";
import { Table, Input, Button, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import "./style.css";

const GenericTable = ({
  title,
  dataSource,
  columns,
  onRowClick,
  enableRowSelection = false,
  onSelectionChange,
  headerButtonText,
  headerButtonIcon,
  onHeaderButtonClick,
  onTableChange,
  loading,
  emptyText,
  rowClassName,
  expandable,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState("");
  const searchInput = useRef(null);

  const rowSelection = enableRowSelection
    ? {
        selectedRowKeys,
        onChange: (keys, rows) => {
          setSelectedRowKeys(keys);
          onSelectionChange?.(keys, rows);
        },
      }
    : undefined;

  const highlightText = (text) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = String(text).split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "yellow",
            color: "#d32f2f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => {
            setSelectedKeys(e.target.value ? [e.target.value] : []);
            setSearchText(e.target.value);
          }}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              confirm();
              setSearchText("");
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const applyHighlightToJSX = (node) => {
    if (typeof node === "string" || typeof node === "number") {
      return highlightText(node);
    }

    if (React.isValidElement(node) && node.props.children) {
      return React.cloneElement(node, {
        children: React.Children.map(node.props.children, (child) =>
          applyHighlightToJSX(child),
        ),
      });
    }

    return node;
  };

  const enhancedColumns = columns.map((col) => ({
    ...col,
    ...(col.enableSearch ? getColumnSearchProps(col.dataIndex) : {}),
    render: (text, record, index) => {
      const content = col.render ? col.render(text, record, index) : text;

      if (col.enableSearch && searchText) {
        return applyHighlightToJSX(content);
      }

      return content;
    },
  }));

  return (
    <div className="table-generic">
      <div
        className="header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <div className="title">{title}</div>
        {headerButtonText && (
          <Button
            className="table-btn"
            type="primary"
            icon={headerButtonIcon}
            onClick={onHeaderButtonClick}
          >
            {headerButtonText}
          </Button>
        )}
      </div>
      <div className="table">
        <Table
          dataSource={dataSource}
          columns={enhancedColumns}
          rowKey={(record, index) => record.key || index}
          pagination={{ pageSize: 10 }}
          bordered
          scroll={{ x: "max-content" }}
          loading={loading}
          locale={emptyText ? { emptyText } : undefined}
          rowClassName={rowClassName}
          expandable={expandable}
          rowSelection={rowSelection}
          onRow={(record) => ({
            onClick: () => onRowClick?.(record),
          })}
          onChange={(pagination, filters, sorter, extra) =>
            onTableChange?.(filters, extra)
          }
        />
      </div>
    </div>
  );
};

export default GenericTable;
